import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import BrandMark from "./BrandMark";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/course", label: "Foundation", testid: "nav-link-course" },
  { to: "/practice-track", label: "Advanced Practice", testid: "nav-link-practice" },
  { to: "/pro-bono", label: "Pro-Bono", testid: "nav-link-probono" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="global-navbar"
      className={`sticky top-0 z-50 backdrop-blur-md transition-colors ${
        scrolled
          ? "bg-[rgba(255,251,245,0.88)] border-b border-[rgba(74,63,57,0.10)]"
          : "bg-[rgba(255,251,245,0.55)]"
      }`}
    >
      <div className="container-narrow flex items-center justify-between h-16 sm:h-20 gap-4">
        <Link to="/" className="focus-ring rounded-md" aria-label="The Human Work Project home">
          <BrandMark />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-medium transition-colors focus-ring ${
                  isActive
                    ? "text-terracotta"
                    : "text-ink-muted hover:text-ink hover:bg-[rgba(200,106,74,0.08)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="nav-primary-cta-button"
                className="ml-2 bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl px-5 transition-colors"
              >
                Apply for December 2026
                <ChevronDown className="ml-1.5 w-4 h-4 opacity-90" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-72 bg-paper border-[rgba(74,63,57,0.10)] shadow-[0_18px_50px_rgba(31,26,23,0.12)] rounded-2xl p-2"
            >
              <DropdownMenuLabel className="text-[10.5px] uppercase tracking-[0.18em] text-ink-muted font-semibold px-3 pt-2 pb-1">
                Cohort 02 · December 2026
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link
                  to="/course#enroll"
                  data-testid="nav-cta-foundation-link"
                  className="!flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl cursor-pointer focus:bg-[rgba(200,106,74,0.10)]"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-terracotta" />
                    <span className="font-display text-[15px] text-ink whitespace-nowrap">Foundation Track</span>
                  </span>
                  <span className="ml-4 text-[12.5px] text-ink-muted leading-snug">
                    For Grade 11–12, UG & non-psych backgrounds
                  </span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[rgba(74,63,57,0.08)] my-1" />
              <DropdownMenuItem asChild>
                <Link
                  to="/practice-track#enroll"
                  data-testid="nav-cta-practice-link"
                  className="!flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl cursor-pointer focus:bg-[rgba(95,127,107,0.10)]"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sage" />
                    <span className="font-display text-[15px] text-ink whitespace-nowrap">Advanced Practice Track</span>
                  </span>
                  <span className="ml-4 text-[12.5px] text-ink-muted leading-snug">
                    For PG psychology students & early-career therapists
                  </span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-nav-open-button"
                aria-label="Open menu"
                className="p-2 rounded-lg hover:bg-[rgba(200,106,74,0.10)] focus-ring"
              >
                <Menu className="w-6 h-6 text-ink" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-paper border-clay w-[88%] sm:max-w-sm">
              <div className="flex items-center justify-between mb-6">
                <BrandMark />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="p-2 rounded-lg hover:bg-[rgba(200,106,74,0.10)]"
                >
                  <X className="w-5 h-5 text-ink" />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `px-3 py-3 rounded-lg text-base font-medium ${
                        isActive ? "text-terracotta bg-[rgba(200,106,74,0.08)]" : "text-ink"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="mt-4 pt-4 border-t border-[rgba(74,63,57,0.10)]">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2 px-1">
                    Apply for Cohort 02 · December 2026
                  </div>
                  <Link to="/course#enroll" onClick={() => setOpen(false)}>
                    <Button
                      data-testid="mobile-nav-cta-foundation"
                      className="w-full bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl mb-2"
                    >
                      Foundation Track
                    </Button>
                  </Link>
                  <Link to="/practice-track#enroll" onClick={() => setOpen(false)}>
                    <Button
                      data-testid="mobile-nav-cta-practice"
                      className="w-full bg-[#5F7F6B] hover:bg-[#506e5c] text-white rounded-xl"
                    >
                      Advanced Practice Track
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
